import { Icon, SignedUrlForImage, Table2 } from "components"
import { useMemo } from "react"
import { addLeadingZeros, convertIOSDateToHTMLFormatTime, ordinalSuffix } from "config/utils"
import tickMark from "images/tickMark.png"
import crossMark from "images/crossMark.png"
import viewOnMapMark from "images/viewOnMapMark.png"
import { PreviousShiftEvent } from "@type/workMonitor.types"
import cx from "classnames"
import { twMerge } from "tailwind-merge"
import { Image } from "antd-v5"

interface Props {
	inventoryEvents: PreviousShiftEvent[]
}

const InventoryTable: React.FC<Props> = ({ inventoryEvents }) => {
	if (!inventoryEvents.length) return null
	const { rows, columns, failedInventoryCalls, totalInventoryCalls, submittedInventoryCalls } = useMemo(() => {
		const totalInventoryCalls = inventoryEvents.length
		const failedInventoryCalls = inventoryEvents.filter(e => e.isEventMissed).length
		const submittedInventoryCalls = totalInventoryCalls - failedInventoryCalls

		const rows = inventoryEvents.map(({ dueTime, eventTime, isEventMissed, geo, images }, i) => {
			const dueTimeFormatted = dueTime ? convertIOSDateToHTMLFormatTime(dueTime) : "-"
			const submitTimeFormatted = eventTime ? convertIOSDateToHTMLFormatTime(eventTime) : "-"
			const passed = !isEventMissed

			return {
				index: i + 1,
				dueTimeFormatted,
				submitTimeFormatted,
				passed,
				geo,
				images,
				...(!!images?.length && {
					renderAdditionalRow: () => (
						<SignedUrlForImage path={images ?? []}>
							{(signedUrls, loading) => {
								return (
									<div className="flex flex-wrap gap-3 p-3">
										{signedUrls.map(url => {
											return loading ? (
												<div className="flex justify-center">
													<Icon name="spinner" color="black" className="animate-spin " />
												</div>
											) : (
												<Image.PreviewGroup>
													<Image width={75} height={110} key={url} src={url} alt="avatar" />
												</Image.PreviewGroup>
											)
										})}
									</div>
								)
							}}
						</SignedUrlForImage>
					),
				}),
			}
		})

		const columns = [
			{
				label: "number",
				key: "number",
				className: "border border-0.125 border-[#667085] pl-3 py-3",
				render: ({ row }: any) => (
					<div className={twMerge(cx("text-gray-900", { "text-[#B42318]": !row.passed }))}>
						{`${row.index}${ordinalSuffix(row.index)} INVENTORY CHECK `}
					</div>
				),
			},
			{
				label: "Due Time",
				key: "dueTime",
				className: "border border-0.125 border-[#667085] pl-3 py-3",
				render: ({ row }: any) => (
					<div className={`space-x-2.5 ${!row.passed ? "text-[#B42318]" : "text-gray-500"}`}>
						<span>DUE TIME: </span>
						<span className={`space-x-2.5  ${!row.passed ? "text-[#B42318]" : "text-gray-900"}`}>
							{row.dueTimeFormatted}
						</span>
					</div>
				),
			},
			{
				label: "Submit Time",
				key: "submitTime",
				className: "border border-0.125 border-[#667085] pl-3 py-3",
				render: ({ row }: any) => (
					<div className={`space-x-2.5 ${!row.passed ? "text-[#B42318]" : "text-gray-500"}`}>
						<span>SUBMIT TIME:</span>
						<span className="text-gray-900">{row.submitTimeFormatted}</span>
					</div>
				),
			},
			{
				label: "Location",
				key: "location",
				className: "border border-0.125 border-[#667085] pl-3",
				render: ({ row }: any) => {
					const hasCoordinates = row.geo?.coordinates?.length === 2

					return (
						<div className="flex items-center space-x-2.5 text-gray-500">
							<div>LOCATION:</div>
							{hasCoordinates ? (
								<a
									href={`https://www.google.com/maps?q=${row.geo.coordinates[1]},${row.geo.coordinates[0]}`}
									target="_blank"
									rel="noreferrer"
								>
									<div className="flex items-center space-x-1.5 rounded-7.5 border-0.125 border-[#175CD3]  bg-[#EFF8FF] py-1 px-3 text-[#175CD3]">
										<span>View On Map</span>
										<img className="h-5 w-5" src={viewOnMapMark} alt="arrow" />
									</div>
								</a>
							) : (
								<span>-</span>
							)}
						</div>
					)
				},
			},
			{
				label: "Status",
				key: "status",
				className: "border border-0.125 border-[#667085] pl-3 py-3",
				render: ({ row }: any) => (
					<div className="flex items-center space-x-2.5 text-gray-500">
						<span>STATUS:</span>
						<span
							className={cx("w-12.5 ml-2.5 flex items-center gap-1 rounded-4 px-2 font-medium", {
								"bg-[#ECFDF3] text-[#027A48]": row.passed,
								"bg-[#FEF3F2] text-[#B42318]": !row.passed,
							})}
						>
							<img
								className="h-3 w-3"
								src={row.passed ? tickMark : crossMark}
								alt={row.passed ? "tick" : "cross"}
							/>
							{row.passed ? "Passed" : "Failed"}
						</span>
					</div>
				),
			},
		]

		return {
			rows,
			columns,
			failedInventoryCalls,
			totalInventoryCalls,
			submittedInventoryCalls,
		}
	}, [inventoryEvents])

	return (
		<div className="space-y-5">
			<div className="flex w-full justify-between bg-[#B2DDFF] py-3 px-6 text-sm">
				<div className="font-medium">INVENTORY</div>
				<div className="flex space-x-10">
					<div className="flex space-x-3">
						<span className="font-medium">TOTAL:</span>
						<span>{addLeadingZeros(totalInventoryCalls, 2)}</span>
					</div>
					<div className="flex space-x-3">
						<span className="font-medium">SUBMITTED:</span>
						<span>{addLeadingZeros(submittedInventoryCalls, 2)}</span>
					</div>
					<div className="flex space-x-3">
						<span className="font-medium">FAILED:</span>
						<span>{addLeadingZeros(failedInventoryCalls, 2)}</span>
					</div>
				</div>
			</div>

			<Table2
				tableClassName="w-full text-sm"
				hideHeader={true}
				columns={columns}
				rows={rows}
				rowClassName=" pl-2.5 h-0 border border-0.125 border-[#667085]"
			/>
		</div>
	)
}

export default InventoryTable
